/**
 * PrescriptionHelpers — Shared UI helpers for the prescription form.
 *
 * SectionHeader: renders icon + label + (i) info tooltip button.
 * SECTION_INFO: tooltip text keyed by section name.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

/* ── Tooltip descriptions for each prescription section ── */
export const SECTION_INFO = {
  vitals:       'Record patient\u2019s vital signs \u2014 BP, heart rate, temperature, SpO\u2082, weight',
  symptoms:     'Select or type the patient\u2019s presenting symptoms (chief complaints)',
  medicines:    'Prescribe medications \u2014 type name, select dosage, timing, and duration',
  tests:        'Add lab or imaging tests — printed, not ordered; reception raises the lab orders',
  notes:        'Add clinical observations visible on the prescription',
  diagnosis:    'Enter the clinical diagnosis \u2014 suggestions are based on your past prescriptions',
  instructions: 'Add patient instructions (diet, activity, precautions) \u2014 one per line',
  followUp:     'Set when the patient should return for a follow-up visit',
};

/**
 * PadField — P2: ONE section-label treatment in ONE fixed gutter.
 *
 * Every section of the prescription pad is `label | field`, with the label in a
 * fixed 7rem column, so every input on the pad starts at the same x. The label
 * itself is `SectionHeader`'s default treatment (0.75rem uppercase 600 muted,
 * no emoji, no trailing colon) — the five per-call `style` overrides the pad
 * used to carry are gone.
 *
 * Below `GUTTER_STACK_PX` of available width the label sits ABOVE its field
 * instead: a 7rem gutter inside the Visit Actions accordion would leave the
 * input unusable.
 */
export const SECTION_LABEL_GUTTER = '7rem';
export const GUTTER_STACK_PX = 420;

/**
 * `hint` — an optional small muted line rendered directly under the section
 * label (rx-allergies P-FE-1: the ratified `From patient record` attribution).
 * Nothing renders when it is absent, so every other section is byte-unchanged.
 */
export function PadField({ label, icon, tooltip, isDark, dataTour, hint, children, style }) {
  const hostRef = useRef(null);
  const [stacked, setStacked] = useState(false);
  useEffect(() => {
    const measure = () => {
      const w = hostRef.current?.getBoundingClientRect().width || 0;
      setStacked((prev) => {
        const next = w > 0 && w < GUTTER_STACK_PX;
        return prev === next ? prev : next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div
      ref={hostRef}
      data-tour={dataTour}
      data-testid={`pad-field-${String(label).toLowerCase().replace(/[^a-z]+/g, '-')}`}
      style={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        // Wrap, never overflow: the pad's wrapper is `overflow: hidden`.
        flexWrap: 'wrap',
        alignItems: stacked ? 'stretch' : 'flex-start',
        gap: stacked ? '0.2rem' : '0.5rem',
        position: 'relative',
        ...style,
      }}
    >
      <div style={{
        width: stacked ? 'auto' : SECTION_LABEL_GUTTER,
        flexShrink: 0, paddingTop: stacked ? 0 : '0.4rem',
      }}>
        <SectionHeader icon={icon} label={label} tooltip={tooltip} isDark={isDark} />
        {hint && (
          <div style={{
            marginTop: '0.15rem', fontSize: '0.625rem', fontWeight: 500, lineHeight: 1.3,
            // Solid, readable in BOTH modes (CLAUDE.md day-mode contrast rule) —
            // muted, never a low-alpha wash.
            color: isDark ? '#94a3b8' : '#64748b',
            fontFamily: 'system-ui, sans-serif',
          }}>{hint}</div>
        )}
      </div>
      {/* The field column wraps: a long-list row (follow-up presets, symptom
          chips) must never push a horizontal overflow into the pad. */}
      <div style={{
        flex: 1, minWidth: 0, position: 'relative',
        display: 'flex', flexDirection: 'column', flexWrap: 'wrap',
      }}>{children}</div>
    </div>
  );
}

/**
 * SectionHeader
 *
 * Usage:
 *   <SectionHeader icon="🩺" label="Diagnosis" tooltip={SECTION_INFO.diagnosis} isDark={isDark} />
 *
 * Renders inline-flex: icon + label + small (i) button.
 * Clicking (i) toggles a fixed-position tooltip card.
 */
/**
 * FE-33 — the ONE heading treatment for every section on the pad. Bold, in the
 * pad's primary ink, at the size the D22 scale already sets.
 */
export const PAD_LABEL_WEIGHT = 700;
export const padLabelInk = (isDark) => (isDark ? '#f1f5f9' : '#0f172a');

export function SectionHeader({ icon, label, tooltip, isDark, style }) {
  const [showTip, setShowTip] = useState(false);
  const [tipPos, setTipPos] = useState({ top: 0, left: 0 });
  const tipRef = useRef(null);
  const btnRef = useRef(null);

  /* position tooltip using fixed coords from button rect */
  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setTipPos({ top: r.bottom + 6, left: Math.max(8, r.left) });
    }
  }, []);

  /* close on outside click */
  const handleOutside = useCallback((e) => {
    if (
      tipRef.current && !tipRef.current.contains(e.target) &&
      btnRef.current && !btnRef.current.contains(e.target)
    ) {
      setShowTip(false);
    }
  }, []);

  /* close on Escape */
  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') setShowTip(false);
  }, []);

  useEffect(() => {
    if (showTip) {
      updatePos();
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('keydown', handleKey);
      window.addEventListener('scroll', updatePos, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [showTip, handleOutside, handleKey, updatePos]);

  const tipBg    = isDark ? '#1e293b' : '#ffffff';
  const tipBorder = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
  const tipText  = isDark ? '#e2e8f0' : '#334155';
  const infoBg   = isDark ? 'rgba(13,148,136,0.18)' : 'rgba(13,148,136,0.12)';
  const infoColor = isDark ? '#2dd4bf' : '#0f766e';

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', ...style }}>
      {icon && <span style={{ fontSize: '0.8125rem', lineHeight: 1 }}>{icon}</span>}
      {/* FE-33 — EVERY section heading on the pad is bold and in full-contrast
          ink, decided HERE and nowhere else: Vitals, C/O, Symptoms, Medicines,
          Tests, Diagnosis, Instructions, Follow-up, Images and the Templates
          strip all render through this one component, so no section can be
          quieter than another. Size is untouched (the D22 scale owns it) and
          there is no colour block — weight and ink only. */}
      {/* FIX-1 (owner) — a label that fits its short siblings ("Follow-up",
          "Instructions") never needed to wrap, so `nowrap` went unnoticed
          until "Advised surgeries" (17 chars) was too wide for the 7rem
          gutter and visibly ran into the field beside it. `normal` lets it
          wrap at the existing word space, same as any other long label in
          this gutter would — never a clipped/ellipsised label. */}
      <span data-testid="pad-section-label" style={{
        fontSize: style?.fontSize || '0.8125rem',
        fontWeight: style?.fontWeight || PAD_LABEL_WEIGHT,
        color: style?.color || padLabelInk(isDark),
        fontFamily: 'system-ui, sans-serif',
        whiteSpace: 'normal',
        textTransform: style?.textTransform ?? 'uppercase',
      }}>
        {label}
      </span>

      {tooltip && (
        <button
          ref={btnRef}
          onClick={() => setShowTip(v => !v)}
          aria-label={`Info about ${label}`}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '1.25rem', height: '1.25rem', borderRadius: '50%',
            border: `1.5px solid ${infoColor}`, cursor: 'pointer',
            padding: 0, lineHeight: 1,
            fontSize: '0.8125rem', fontWeight: 800, fontStyle: 'italic',
            fontFamily: 'Georgia, serif',
            background: infoBg, color: infoColor,
            transition: 'transform 0.12s ease, background 0.12s ease',
            flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.2)'; e.currentTarget.style.background = isDark ? 'rgba(13,148,136,0.3)' : 'rgba(13,148,136,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.background = infoBg; }}
        >
          i
        </button>
      )}

      {showTip && tooltip && (
        <div
          ref={tipRef}
          style={{
            position: 'fixed',
            top: tipPos.top,
            left: tipPos.left,
            zIndex: 9000,
            background: tipBg,
            border: `1px solid ${tipBorder}`,
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem',
            maxWidth: '15rem',
            boxShadow: isDark
              ? '0 8px 24px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)'
              : '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)',
            animation: 'rxTipFadeIn 0.15s ease',
          }}
        >
          <span style={{
            fontSize: '0.75rem', lineHeight: 1.5,
            color: tipText, fontFamily: 'system-ui, sans-serif',
            display: 'block',
          }}>
            {tooltip}
          </span>
        </div>
      )}

      {/* Only the tooltip animates, so the keyframes ride WITH it — every
          heading was carrying this block in its own text content. */}
      {tooltip && (
        <style>{`@keyframes rxTipFadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
      )}
    </span>
  );
}

/**
 * SectionDivider — thin horizontal line between major sections.
 */
export function SectionDivider({ isDark }) {
  return (
    <hr style={{
      border: 'none',
      height: '1px',
      background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
      margin: '0.35rem 0',
    }} />
  );
}

/**
 * Inject global CSS hover styles for prescription chips.
 * Call once from DigitalPrescriptionForm.
 */
export function PrescriptionHoverStyles() {
  return (
    <style>{`
      .rx-chip-hover {
        transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease !important;
      }
      .rx-chip-hover:hover {
        transform: scale(1.08);
        box-shadow: 0 3px 10px rgba(0,0,0,0.12);
      }
    `}</style>
  );
}
